import { Box, Button } from "@modernatx-internal/component-library";
import Image from "next/image";
import React from "react";

import { useMapContext } from "@/finder/googleMaps/GoogleMapsProvider";
import type { ResultsView } from "@/finder/hooks/V2/useFinder";
import { useFinder } from "@/finder/hooks/V2/useFinder";
import useSearchThisArea from "@/finder/hooks/V2/useSearchThisArea";

const SearchThisAreaMarker = React.forwardRef<HTMLImageElement>((_, ref) => {
  return (
    <Image
      ref={ref}
      alt="Custom Marker"
      height={25}
      src={`/assets/images/svgs/crosshairs.svg`}
      style={{ transform: "translateY(50%)" }}
      width={25}
    />
  );
});

SearchThisAreaMarker.displayName = "SearchThisAreaMarker";

export const SearchThisAreaButton = ({ onClick }: { onClick: () => void }) => {
  const { searchThisAreaText } = useFinder();
  const { hasPanned } = useMapContext();

  const handleClick = () => {
    onClick();
  };

  return (
    <Box
      id="search-this-area"
      sx={{
        borderRadius: "radius-2",
        boxShadow: "shadow-200",
        height: "fit-content",
        opacity: hasPanned ? 1 : 0,
        pointerEvents: hasPanned ? "auto" : "none",
        transition: "transition-ease-out-medium",
        visibility: hasPanned ? "visible" : "hidden",
        width: "fit-content"
      }}
    >
      <Button
        aria-label={searchThisAreaText}
        data-track="search-this-area"
        icon="magnifying-glass"
        iconPosition="start"
        size="lg"
        variant="outline"
        onClick={handleClick}
      >
        {searchThisAreaText}
      </Button>
    </Box>
  );
};

export const SearchThisArea = ({ resultsView }: { resultsView: ResultsView }) => {
  const { hasPanned } = useMapContext();
  const isMobile = resultsView !== "desktop";
  const searchLocationMarkerRef = React.useRef<HTMLDivElement | null>(null);

  const { runSearchThisArea } = useSearchThisArea(searchLocationMarkerRef.current, isMobile);

  if (resultsView === "mobileList") {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 4,
          paddingLeft: [0, null, 104],
          pointerEvents: "none",
          position: "absolute",
          width: "100%"
        }}
      >
        <SearchThisAreaButton onClick={runSearchThisArea} />
      </Box>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          height: "100%",
          justifyContent: "center",
          opacity: hasPanned ? 1 : 0,
          paddingLeft: [0, null, 104],
          pointerEvents: "none",
          position: "absolute",
          transition: "transition-ease-out-medium",
          visibility: hasPanned ? "visible" : "hidden",
          width: "100%"
        }}
      >
        <div ref={searchLocationMarkerRef}>
          <SearchThisAreaMarker />
        </div>
      </Box>
    </>
  );
};
